import {
  ChatBotMessageTypes,
  OnboardingFlowStepStatus,
  OnboardingFlowStepTypes,
  Tool,
  useGetAllToolsQuery,
  useSendMessageToBotMutation,
} from '__generated__/api-types-and-hooks'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import {
  AppDynamicChatMessage,
  TextComponent,
  TypingMessage,
  UIComponentProps,
} from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import { AppTypingIndicator } from 'components/AppOnboarding/AppChatbot/AppTypingIndicator/AppTypingIndicator.component'
import { useInfiniteGetMessagesFromBotQuery } from '__generated__/infinite-api-types-and-hooks'
import { LoadingIndicator } from 'stream-chat-react'
import CircleNotchIcon from 'components/Common/SvgIcons/CircleNotchIcon'
import { useFlashParams } from '../../../../hooks/useFlashParams'
import { getTenantId } from 'utils/getTenantId'
import { AppPaperClipIcon } from 'components/Common/AppSvgIcons/AppPaperClipIcon'
import { AppButton } from 'components/Common/AppButton/AppButton.component'
import { AppSendMessageIcon } from 'components/Common/AppSvgIcons/AppSendMessageIcon'
import Avatar from 'appAssets/appImages/Avatar.png'
import { AppModalBox } from 'components/Common/AppModalBox/AppModalBox.component'
import { AppFileUploadField } from 'components/Common/AppFileUploadField/AppFileUploadField.component'
import moment from 'moment-timezone'
import { AppAvatar } from 'components/Common/AppAvatar/AppAvatar.component'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import { helpDesk } from 'utils/helper'
import { findLast, findLastIndex } from 'lodash'
import { ReactComponent as PenIcon } from 'assets/svgs/pen-icon.svg'
import { ReactComponent as CopyIcon } from 'assets/svgs/copy-icon.svg'
import { FormComponent } from 'components/AppOnboarding/AppChatbot/AppDynamicChatMessage/AppDynamicChatMessage.component'
import AppCrossIcon from 'components/Common/AppSvgIcons/AppModalCrossIcon'
import { AppVoicePlayback } from 'components/Common/AppVoicePlayback/AppVoicePlayback.component'
import { useVoicePlayback } from 'hooks/useVoicePlayback'
import { extractTextForTTS } from 'utils/audioUtils'
import copyToClipboard from 'utils/copyToClipboard'
import { useSelector } from 'react-redux'
import { RootState } from 'App'
import { useFeatureFlag } from 'hooks/userFeatureFlag'

interface OnboardingStepMessageResponse {
  stepType?: OnboardingFlowStepTypes
  stepStatus?: OnboardingFlowStepStatus
  content?: UIComponentProps
  progress?: number
}

export interface AppAIChatPopupProps {
  isOpen: boolean
  onClose: () => void
  initialMessage?: string
  sessionId?: string
  uploadedFileId?: string
  uploadFileLoading?: boolean
  logo?: string
  userInitial?: string
  refetchBusinessGoal: () => void
  uploadFileAction?: (options: { files: FileList; isMultiple: boolean; saveFile: boolean }) => void
  title: string | React.ReactNode
  width?: string
}

export const AppAIChatPopup = ({
  isOpen,
  onClose,
  initialMessage,
  sessionId,
  uploadFileAction,
  uploadedFileId,
  uploadFileLoading,
  logo,
  userInitial,
  refetchBusinessGoal,
  title,
  width = 'w-[600px]',
}: AppAIChatPopupProps) => {
  const [messages, setMessages] = useState<
    {
      sender: string
      text?: string
      content?: UIComponentProps
      stepType?: OnboardingFlowStepTypes
      stepStatus?: OnboardingFlowStepStatus
      createdAt?: string
      isEdited?: boolean
      isEditingMessage?: boolean
      editingOriginalMessageIndex?: number
      editingStepType?: OnboardingFlowStepTypes | null
      editingStepContent?: UIComponentProps | null
      editingMessageText?: string
      originalPromptingAssistantIndex?: number
      originalPromptingStepType?: OnboardingFlowStepTypes | null
      originalPromptingStepContent?: UIComponentProps | null
    }[]
  >([])
  const [queuedMessages, setQueuedMessages] = useState<OnboardingStepMessageResponse[]>([])
  const [inputValue, setInputValue] = useState(initialMessage)
  const [editingTextareaValue, setEditingTextareaValue] = useState('')
  const [currentStep, setCurrentStep] = useState<{
    stepType?: OnboardingFlowStepTypes
    stepStatus?: OnboardingFlowStepStatus
  }>({})
  const [processing, setProcessing] = useState(false)
  const chatContainerRef = useRef<HTMLDivElement>(null)
  const [disableButton, setDisableButton] = useState(false)
  const [isUserScrolling, setIsUserScrolling] = useState(false)
  const [isUpload, setIsUpload] = useState(false)
  const [uploadedFile, setUploadedFile] = useState<File[]>()
  const [startUpload, setStartUpload] = useState(false)
  const [showUploadModal, setShowUploadModal] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const {
    isSupported: isVoiceSupported,
    isPlaying: isVoicePlaying,
    activeKey: activeVoiceMessageKey,
    duration: activeVoiceDuration,
    togglePlayback: toggleVoicePlayback,
  } = useVoicePlayback()
  const { data: toolsData, refetch } = useGetAllToolsQuery(
    {
      tenantId: getTenantId(),
    },
    {
      refetchOnWindowFocus: false,
    }
  )
  const tools = (toolsData?.getAllTools?.data as Tool[]) || []
  const user = useSelector((rootState: RootState) => rootState.user.user)
  const showGetHelpButton = useFeatureFlag(user?.id || '', 'release-chatbot-get-help')

  // Fetch the messages with pagination support
  const {
    data: messageHistoryData,
    fetchNextPage,
    hasNextPage,
    isLoading: isMessagesLoading,
    isFetchingNextPage,
    isFetching,
    refetch: refetchMessages,
  } = useInfiniteGetMessagesFromBotQuery(
    {
      sessionId: sessionId ?? '',
    },
    {
      getNextPageParam: (lastPage) => {
        return lastPage.getMessagesFromBot.nextCursor
          ? { cursor: lastPage.getMessagesFromBot.nextCursor }
          : undefined
      },
      refetchOnWindowFocus: false,
      refetchIntervalInBackground: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    }
  )
  const { removeFlashParams } = useFlashParams<{
    connectorName: string
    toolId: string
  }>()

  const convertMessage = (message: {
    __typename?: 'ChatBotMessage'
    role: string
    content: string
    createdAt: string
    isEdited?: boolean | null
  }) => {
    if (message?.content?.startsWith('{')) {
      const content = JSON.parse(message.content)
      return {
        sender: message.role ?? '',
        content,
        createdAt: message.createdAt ?? '',
        isEdited: message.isEdited || false,
      }
    }
    return {
      sender: message.role ?? '',
      text: message.content ?? '',
      createdAt: message.createdAt ?? '',
      isEdited: message.isEdited || false,
    }
  }

  const isExistingMessage = (message) => {
    const isExisting = messages.find(
      (msg) =>
        msg.createdAt === message.createdAt &&
        (msg.text === message.text ||
          JSON.stringify(msg.content) === JSON.stringify(message.content))
    )
    return !isExisting
  }
  const prevSessionIdRef = useRef<string | undefined>()
  useEffect(() => {
    if (prevSessionIdRef.current !== sessionId) {
      // If the sessionId has changed, reset messages and refetch
      setMessages([]) // Clear previous messages
      prevSessionIdRef.current = sessionId
    }
  }, [sessionId])

  const addMessagesFromLatestPage = useCallback(
    (
      pageMessages: {
        __typename?: 'ChatBotMessage'
        role: string
        content: string
        createdAt: string
      }[]
    ) => {
      const newMessages = pageMessages
        .map((message) => convertMessage(message))
        .reverse()
        .filter(Boolean)
      // Avoid adding duplicate messages by checking their createdAt and content
      setMessages((prevMessagesArray) => {
        const existingMessages = new Set(
          prevMessagesArray.map(
            (msg) => `${msg.createdAt}-${msg.text ?? JSON.stringify(msg.content)}`
          )
        )
        const oldMessages =
          newMessages?.filter((msg) => {
            const key = `${msg?.createdAt}-${msg?.text ?? JSON.stringify(msg?.content)}`
            return !existingMessages.has(key)
          }) ?? []
        return [...oldMessages, ...prevMessagesArray]
      })
    },
    []
  )

  useEffect(() => {
    if (!messageHistoryData || isRetrying) return

    const pageMessages = messageHistoryData.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []

    if (!messages.length && pageMessages.length > 1) {
      pageMessages.forEach((page) => {
        addMessagesFromLatestPage(page.getMessagesFromBot.messages ?? [])
      })
      return
    }
    if (lastPageMessages.length) {
      addMessagesFromLatestPage(lastPageMessages)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageHistoryData, sessionId, addMessagesFromLatestPage, messages.length])
  const {
    mutate: sendMessageToBot,
    data: latestResponse,
    isLoading: isSendingMessageTobot,
  } = useSendMessageToBotMutation({
    onError: async (error) => {
      if (error?.message === 'Execution timed out.') {
        const newMessage = 'There was a delay in processing. Please wait a moment...'
        addMessage({ sender: 'assistant', text: newMessage })
        setProcessing(true)
        scrollToBottom(true)
        setIsRetrying(true)

        // Try to fetch new messages for up to 30 seconds
        const intervalId = setInterval(async () => {
          const result = await refetchMessages({
            refetchPage: (_, index) => index === 0,
          })

          if (!result.data) return

          const lastMessage = result.data.pages[0]?.getMessagesFromBot?.messages?.[0]
          const lastCurrentMessage = messages[messages.length - 1]
          // If we found a new message from the bot
          if (
            lastMessage?.role === 'assistant' &&
            lastMessage?.createdAt !== lastCurrentMessage?.createdAt
          ) {
            setProcessing(false)
            const lastPageMessages = result.data.pages[0]?.getMessagesFromBot?.messages ?? []
            const differentMessages = lastPageMessages.filter(
              (message) =>
                !messages.find((m) => m.createdAt === message.createdAt) &&
                message.role === 'assistant'
            )
            // Add the new messages using the existing function
            const convertedMessages = differentMessages
              .map((message) => convertMessage(message))
              .filter((message) => isExistingMessage(message))
              .reverse()
            setMessages((prevMessages) => [...prevMessages, ...convertedMessages])
            const lastMessage = convertedMessages[0]
            if (lastMessage?.content?.type !== OnboardingFlowStepTypes.Message) {
              clearInterval(intervalId)
              setIsRetrying(false)
            }
            scrollToBottom(true)
          }
        }, 5000)
      }
    },
  })

  const handleNewMessage = (message: OnboardingStepMessageResponse) => {
    setCurrentStep(message)
    if ((message?.content as TextComponent)?.text?.toLowerCase().includes('goal center')) {
      refetchBusinessGoal()
    }
    setMessages((prevMessages) => [
      ...prevMessages,
      { sender: 'assistant', content: message.content },
    ])
    setIsUserScrolling(false)
    setProcessing(false)
  }

  useEffect(() => {
    if (latestResponse?.sendMessageToBot.responses?.length && !isRetrying) {
      const firstResponse = JSON.parse(
        latestResponse?.sendMessageToBot.responses[0]
      ) as OnboardingStepMessageResponse
      handleNewMessage(firstResponse)
      setQueuedMessages((prevMessages) => {
        const newMessages = latestResponse?.sendMessageToBot
          .responses!.slice(1)
          .map((response) => JSON.parse(response) as OnboardingStepMessageResponse)
        return [...prevMessages, ...newMessages]
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestResponse?.sendMessageToBot.responses])

  useEffect(() => {
    const lastMessage = messages[messages.length - 1]
    const lastAssistantMessage = findLast(messages, (message) => message.sender === 'assistant')
    if (lastMessage?.sender === 'user') {
      scrollToBottom(true)
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.End) {
      onClose() // Close the popup when flow ends
    }
    if (lastAssistantMessage?.content?.type === OnboardingFlowStepTypes.ToolConnection) {
      const content = lastAssistantMessage.content as { tools: Tool[] }
      const toolConnections = content.tools
      const connectedTools = toolConnections.filter((tool) =>
        tools.find((t) => t.id === tool.id && t.isConnected)
      )
      if (toolConnections.length === connectedTools.length && !isSendingMessageTobot) {
        setProcessing(true)
        sendMessageToBot(
          {
            input: {
              userMessage: JSON.stringify({ toolIds: connectedTools.map((tool) => tool.id) }),
              userMessageType: ChatBotMessageTypes.ToolConnected,
              sessionId,
            },
          },
          {
            onError: (error) => {
              if (error.statusCode === 401) {
                window.location.reload()
              }
            },
          }
        )
        removeFlashParams(['toolId'])
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, tools])

  const submitDataConfirmationForm = async (formData) => {
    const filesFields = ['businessPlanFile']
    const filesToUpload = filesFields.filter((field) => formData[field])

    if (filesToUpload.length > 0) {
      filesToUpload.forEach((field) => {
        uploadFileAction?.({
          files: [formData[field]] as unknown as FileList,
          isMultiple: false,
          saveFile: true,
        })
      })
      return
    }
    setProcessing(true)
    const { id, ...data } = formData
    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(data),
          userMessageType: ChatBotMessageTypes.FormResponse,
          ...(formData.skip ? { skip: true } : {}),
          sessionId,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
  }

  const handleSendMessage = useCallback(async () => {
    const pageMessages = messageHistoryData?.pages ?? []
    const page = pageMessages.length
      ? pageMessages[pageMessages.length - 1]
      : { getMessagesFromBot: { messages: [] } }
    const lastPageMessages = page.getMessagesFromBot.messages ?? []
    if (!inputValue && lastPageMessages.length) return
    setProcessing(true)
    const userMessage = { sender: 'user', text: inputValue }
    addMessage(userMessage)
    setInputValue('')
    sendMessageToBot(
      {
        input: {
          userMessage: inputValue ?? '',
          sessionId,
          userMessageType: ChatBotMessageTypes.Text,
        },
      },
      {
        onError: (error) => {
          if (error.statusCode === 401) {
            window.location.reload()
          }
        },
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue, messages])

  useEffect(() => {
    if (isMessagesLoading || isFetchingNextPage || isFetching) {
      return
    }
    const pageMessages = messageHistoryData?.pages ?? []
    const lastPageMessages =
      pageMessages[pageMessages.length - 1]?.getMessagesFromBot.messages ?? []
    if (messages.length || lastPageMessages.length) {
      return
    }
    if (!messages.length && !lastPageMessages.length && isOpen) {
      handleSendMessage()
    }
  }, [
    handleSendMessage,
    isFetching,
    isFetchingNextPage,
    isMessagesLoading,
    messageHistoryData?.pages,
    messages.length,
    isOpen,
  ])

  useEffect(() => {
    if (!uploadFileLoading && uploadedFileId) {
      submitDataConfirmationForm({ businessPlan: uploadedFileId })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadFileLoading, uploadedFileId])

  const scrollToBottom = (force?: boolean) => {
    if (!chatContainerRef.current) return
    if (!isUserScrolling || force) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight
    }
  }

  const scrollTopMessage = () => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollTop + 250
    }
  }

  const handleUserScroll = () => {
    if (chatContainerRef.current) {
      const isAtBottom =
        chatContainerRef.current.scrollHeight - chatContainerRef.current.scrollTop ===
        chatContainerRef.current.clientHeight
      setIsUserScrolling(!isAtBottom)

      // Trigger loading more messages when the user scrolls to the top
      if (chatContainerRef.current.scrollTop === 0 && hasNextPage) {
        fetchNextPage()
      }
    }
  }

  const handleTypingMessage = (text: string, displayText: string) => {
    if (text === displayText) {
      setDisableButton(false)
      if (queuedMessages.length && text) {
        const firstQueueMessage = queuedMessages.shift()
        handleNewMessage(firstQueueMessage!)
        setQueuedMessages(queuedMessages)
        return
      }
    } else {
      if (text?.length) {
        setDisableButton(true)
      }
    }
    if (displayText.length) {
      scrollToBottom()
    } else {
      scrollTopMessage()
    }
  }

  const addMessage = (newMessage) => {
    setMessages((prevMessages) => [...prevMessages, newMessage])
    setIsUserScrolling(false)
  }

  const handleEditLastMessage = () => {
    const lastUserMessage = findLast(messages, (msg) => msg.sender === 'user')
    if (lastUserMessage) {
      // Find the assistant message that prompted the last user message
      const lastUserMessageIndex = findLastIndex(
        messages,
        (msg) => msg.sender === 'user' && !msg.isEdited
      )

      let stepType: OnboardingFlowStepTypes | null = null
      let stepContent: UIComponentProps | null = null
      let messageText = lastUserMessage.text || ''
      let promptingAssistantIndex: number | undefined

      // Check if this message has stored original prompting information (from previous edits)
      if (lastUserMessage.originalPromptingAssistantIndex !== undefined) {
        // Use the stored original prompting assistant message
        promptingAssistantIndex = lastUserMessage.originalPromptingAssistantIndex
        const promptingAssistantMessage = messages[promptingAssistantIndex]

        if (promptingAssistantMessage && promptingAssistantMessage.content?.type) {
          stepType = promptingAssistantMessage.content.type as OnboardingFlowStepTypes

          if (
            stepType === OnboardingFlowStepTypes.Form ||
            stepType === OnboardingFlowStepTypes.GoalTemplate
          ) {
            // For form steps, we need to re-show the form
            stepContent = (promptingAssistantMessage.content as FormComponent)
              ? ({
                  ...promptingAssistantMessage.content,
                  status: OnboardingFlowStepStatus.InProgress,
                } as FormComponent)
              : null
          } else {
            // For question/text steps, show text input
            stepType = OnboardingFlowStepTypes.Question
            stepContent = null
          }
        } else if (lastUserMessage.originalPromptingStepType) {
          // Fallback to stored step type and content
          stepType = lastUserMessage.originalPromptingStepType
          stepContent = lastUserMessage.originalPromptingStepContent || null
        }
      } else {
        // First time editing - find the assistant message immediately before the last user message
        promptingAssistantIndex = lastUserMessageIndex - 1
        const promptingAssistantMessage = messages[promptingAssistantIndex]

        // Get the step type from the assistant message that prompted the user's response
        if (promptingAssistantMessage && promptingAssistantMessage.content?.type) {
          stepType = promptingAssistantMessage.content.type as OnboardingFlowStepTypes

          if (
            stepType === OnboardingFlowStepTypes.Form ||
            stepType === OnboardingFlowStepTypes.GoalTemplate
          ) {
            // For form steps, we need to re-show the form
            stepContent = (promptingAssistantMessage.content as FormComponent)
              ? ({
                  ...promptingAssistantMessage.content,
                  status: OnboardingFlowStepStatus.InProgress,
                } as FormComponent)
              : null
          } else {
            // For question/text steps, show text input
            stepType = OnboardingFlowStepTypes.Question
            stepContent = null
          }
        } else {
          // Default to text input if we can't determine the step type
          stepType = OnboardingFlowStepTypes.Question
          stepContent = null
        }
      }

      // Pre-fill only the editing textarea (not the main input)
      setEditingTextareaValue(messageText)
      setInputValue('') // Keep main input empty

      // Add editing message as a bot message
      const editingMessage = {
        sender: 'assistant',
        text: messageText,
        isEditingMessage: true,
        editingOriginalMessageIndex: lastUserMessageIndex,
        editingStepType: stepType,
        editingStepContent: stepContent,
        editingMessageText: messageText,
        createdAt: new Date().toISOString(),
      }

      setMessages((prev) => [...prev, editingMessage])
      scrollToBottom(true)
    }
  }

  const handleCancelEdit = () => {
    // Remove the editing message from the messages array
    setMessages((prev) => prev.filter((msg) => !msg.isEditingMessage))
    setEditingTextareaValue('')
    setInputValue('')
  }

  const handleSaveEdit = () => {
    const trimmedText = (editingTextareaValue || '').trim()
    if (!trimmedText) {
      handleCancelEdit()
      return
    }

    // Find the editing message
    const editingMessageIndex = messages.findIndex((msg) => msg.isEditingMessage)
    if (editingMessageIndex === -1) return

    const editingMessage = messages[editingMessageIndex]
    const originalMessageIndex = editingMessage.editingOriginalMessageIndex

    if (originalMessageIndex === undefined) return

    // Get the original message to preserve its prompting assistant info
    const originalMessage = messages[originalMessageIndex]

    // Determine the prompting assistant index - use stored value or calculate from editing message
    let promptingAssistantIndex: number | undefined =
      originalMessage?.originalPromptingAssistantIndex
    let promptingStepType: OnboardingFlowStepTypes | null | undefined =
      originalMessage?.originalPromptingStepType
    let promptingStepContent: UIComponentProps | null | undefined =
      originalMessage?.originalPromptingStepContent

    // If not stored, use the editing message's step info and find the matching assistant message
    if (promptingAssistantIndex === undefined && editingMessage.editingStepType) {
      // Find the assistant message that matches the step type/content, looking backwards from original message
      for (let idx = originalMessageIndex - 1; idx >= 0; idx--) {
        const msg = messages[idx]
        if (msg.sender === 'assistant' && msg.content?.type === editingMessage.editingStepType) {
          promptingAssistantIndex = idx
          promptingStepType = editingMessage.editingStepType
          promptingStepContent = editingMessage.editingStepContent
          break
        }
      }
    }

    // Keep entire history, mark original as edited, and append the new message
    setMessages((prev) => {
      const filteredMessages = prev.filter((msg) => !msg.isEditingMessage)
      const isLastMessage = originalMessageIndex === filteredMessages.length - 1

      const updatedMessage = {
        sender: 'user' as const,
        text: trimmedText,
        originalPromptingAssistantIndex: promptingAssistantIndex,
        originalPromptingStepType: promptingStepType,
        originalPromptingStepContent: promptingStepContent,
      }

      if (isLastMessage) {
        // If editing the last message, replace it
        return filteredMessages.map((msg, idx) =>
          idx === originalMessageIndex ? { ...msg, isEdited: true, ...updatedMessage } : msg
        )
      }

      // Otherwise, keep history and append
      const updatedHistory = filteredMessages.map((msg, idx) =>
        idx === originalMessageIndex ? { ...msg, isEdited: true } : msg
      )
      return [...updatedHistory, updatedMessage]
    })
    setIsUserScrolling(false)

    // Set processing state to show loading
    setProcessing(true)

    // Send edited message with the edit flag
    sendMessageToBot(
      {
        input: {
          userMessage: trimmedText,
          sessionId: sessionId ?? '',
          userMessageType: ChatBotMessageTypes.Text,
          isEditingLastMessage: true,
        },
      },
      {
        onSuccess: (response) => {
          // Reset inputs
          setEditingTextareaValue('')
          setInputValue('')
          setIsRetrying(false)
          // Note: Don't set processing to false here, let the normal response handler do it
        },
        onError: (error) => {
          setProcessing(false)
          console.error('Edit submission failed:', error)
        },
      }
    )
  }

  const handleEditFormSubmit = (formData: Record<string, string | string[] | Date | Date[]>) => {
    // Find the editing message
    const editingMessageIndex = messages.findIndex((msg) => msg.isEditingMessage)
    if (editingMessageIndex === -1) return

    const editingMessage = messages[editingMessageIndex]
    const originalMessageIndex = editingMessage.editingOriginalMessageIndex

    if (originalMessageIndex === undefined) return

    // Get the original message to preserve its prompting assistant info
    const originalMessage = messages[originalMessageIndex]

    // Determine the prompting assistant index - use stored value or calculate from editing message
    let promptingAssistantIndex: number | undefined =
      originalMessage?.originalPromptingAssistantIndex
    let promptingStepType: OnboardingFlowStepTypes | null | undefined =
      originalMessage?.originalPromptingStepType
    let promptingStepContent: UIComponentProps | null | undefined =
      originalMessage?.originalPromptingStepContent

    // If not stored, use the editing message's step info and find the matching assistant message
    if (promptingAssistantIndex === undefined && editingMessage.editingStepType) {
      // Find the assistant message that matches the step type/content, looking backwards from original message
      for (let idx = originalMessageIndex - 1; idx >= 0; idx--) {
        const msg = messages[idx]
        if (msg.sender === 'assistant' && msg.content?.type === editingMessage.editingStepType) {
          promptingAssistantIndex = idx
          promptingStepType = editingMessage.editingStepType
          promptingStepContent = editingMessage.editingStepContent
          break
        }
      }
    }

    // Format the response (exclude 'id' field which contains the form title)
    const { id, ...dataWithoutId } = formData

    // Get form fields from the editing step content to format values properly
    const stepContent = editingMessage.editingStepContent as FormComponent | null
    const formFields = stepContent?.formFields || []

    // Format each field value based on its type
    const formattedValues: string[] = []
    Object.entries(dataWithoutId).forEach(([fieldName, fieldValue]) => {
      const formField = formFields.find((f) => f.name === fieldName)

      if (!formField || !fieldValue) {
        // If no field found or value is empty, skip or use value as-is
        if (fieldValue) {
          formattedValues.push(String(fieldValue))
        }
        return
      }

      // Handle date fields - format as MM/DD/YYYY
      if (formField.fieldType === 'date') {
        try {
          // Handle array of dates (for multiple date selection)
          if (Array.isArray(fieldValue)) {
            const formattedDates = fieldValue
              .map((date) => {
                const dateValue = moment(date)
                return dateValue.isValid() ? dateValue.format('MM/DD/YYYY') : String(date)
              })
              .filter(Boolean)
            if (formattedDates.length > 0) {
              formattedValues.push(formattedDates.join(', '))
            }
          } else {
            // Handle single date (could be Date object, string, or ISO string)
            const dateValue = moment(fieldValue instanceof Date ? fieldValue : fieldValue)
            if (dateValue.isValid()) {
              formattedValues.push(dateValue.format('MM/DD/YYYY'))
            } else {
              formattedValues.push(String(fieldValue))
            }
          }
        } catch {
          formattedValues.push(String(fieldValue))
        }
      }
      // Handle checkbox and select fields - map values to labels
      else if (
        formField.fieldType === 'checkbox' ||
        formField.fieldType === 'select' ||
        formField.fieldType === 'buttonSelect'
      ) {
        if (formField.options) {
          // Handle array values (for multiple selections)
          const values = Array.isArray(fieldValue) ? fieldValue : [fieldValue]
          const labels = values
            .map((val) => {
              // Convert value to string for comparison (handles Date objects, etc.)
              const stringVal = val instanceof Date ? moment(val).format('MM/DD/YYYY') : String(val)
              const option = formField.options?.find(
                (opt) => opt.value === stringVal || opt.value === val
              )
              return option?.label || stringVal
            })
            .filter(Boolean)
          if (labels.length > 0) {
            formattedValues.push(labels.join(', '))
          }
        } else {
          formattedValues.push(String(fieldValue))
        }
      }
      // For other field types, use value as-is
      else {
        formattedValues.push(String(fieldValue))
      }
    })

    const formattedResponse = formattedValues.join(', ')

    // Keep entire history, mark original as edited, and append the new message
    setMessages((prevMessages) => {
      const filteredMessages = prevMessages.filter((msg) => !msg.isEditingMessage)
      const isLastMessage = originalMessageIndex === filteredMessages.length - 1

      const updatedMessage = {
        sender: 'user' as const,
        text: formattedResponse,
        originalPromptingAssistantIndex: promptingAssistantIndex,
        originalPromptingStepType: promptingStepType,
        originalPromptingStepContent: promptingStepContent,
      }

      if (isLastMessage) {
        // If editing the last message, replace it
        return filteredMessages.map((msg, idx) =>
          idx === originalMessageIndex ? { ...msg, isEdited: true, ...updatedMessage } : msg
        )
      }

      // Otherwise, keep history and append
      const updatedHistory = filteredMessages.map((msg, idx) =>
        idx === originalMessageIndex ? { ...msg, isEdited: true } : msg
      )
      return [...updatedHistory, updatedMessage]
    })
    setIsUserScrolling(false)

    // Set processing state to show loading
    setProcessing(true)

    sendMessageToBot(
      {
        input: {
          userMessage: JSON.stringify(formData),
          sessionId: sessionId ?? '',
          userMessageType: ChatBotMessageTypes.FormResponse,
          isEditingLastMessage: true,
        },
      },
      {
        onSuccess: (response) => {
          setIsRetrying(false)
          // Note: Don't set processing to false here, let the normal response handler do it
        },
        onError: (error) => {
          setProcessing(false)
          console.error('Edit form submission failed:', error)
        },
      }
    )
    scrollToBottom()
  }

  const lastMessageContent = messages[messages.length - 1]?.content
  const isFormStep =
    currentStep.stepType === OnboardingFlowStepTypes.Form ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.Form)
  const isToolConnectionStep =
    currentStep.stepType === OnboardingFlowStepTypes.ToolConnection ||
    (lastMessageContent?.type && lastMessageContent.type === OnboardingFlowStepTypes.ToolConnection)

  if (!isOpen) return null

  return (
    <AppModalBox
      title={title}
      onClose={onClose}
      width={width}
      backgroundColor="bg-app-grey-5"
      height="h-[80vh] min-h-[700px]"
      position="center"
      childClass="p-0"
      rounded="xl"
      showBorder={false}
    >
      <div className="flex flex-col gap-4 w-full rounded-lg bg-app-grey-5">
        <div className="p-2 sm:p-4 pb-0 bg-app-grey-5">
          <div className="w-full bg-app-grey-5 font-normal font-inter text-center text-sm text-grey-lighter6x">
            {`Today, ${moment().format('hh:mm A')}`}
          </div>
        </div>
        <div
          className="flex flex-col w-full p-2 sm:p-4 pt-0 overflow-y-scroll h-[400px] gap-8"
          ref={chatContainerRef}
          onScroll={handleUserScroll}
        >
          {isFetchingNextPage && (
            <div className="flex justify-center p-1">
              <LoadingIndicator size={20} />
            </div>
          )}
          {messages.map((message, index) => {
            const isLastMessage = index === messages.length - 1
            const lastUserMessageIndex = findLastIndex(messages, (msg) => msg.sender === 'user')
            const isLastUserMessage = index === lastUserMessageIndex
            const messageKey = message.createdAt ?? `message-${index}`
            const assistantVoiceText =
              message.sender === 'assistant' && message.text ? extractTextForTTS(message.text) : ''
            const assistantContentVoiceText =
              message.sender === 'assistant' && message.content
                ? extractTextForTTS(message.content)
                : ''
            const showVoicePlayback = isVoiceSupported && !!assistantVoiceText
            const showContentVoicePlayback = isVoiceSupported && !!assistantContentVoiceText
            const isVoiceActive = activeVoiceMessageKey === messageKey

            // Check if this is an editing message
            if (message.isEditingMessage) {
              return (
                <React.Fragment key={messageKey}>
                  <div className="flex gap-2 justify-start">
                    <div>
                      <AppAvatar
                        size={'md'}
                        type="image"
                        shape={'Rounded'}
                        text={userInitial}
                        color="Primary"
                        src={Avatar}
                      />
                    </div>
                    <div className="flex-1 max-w-[90%]">
                      {/* Header with pencil icon, "Editing message" text, and X icon */}
                      <div className="flex justify-between items-center mb-3 p-3 bg-app-grey-20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <PenIcon className="w-5 h-5" />
                          <span className="text-sm font-medium text-app-grey-100">
                            Editing message
                          </span>
                        </div>
                        <button
                          onClick={handleCancelEdit}
                          className="p-1 hover:bg-app-grey-20 rounded transition-colors"
                          aria-label="Cancel editing"
                        >
                          <AppCrossIcon className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Render form or text based on step type */}
                      {message.editingStepType === OnboardingFlowStepTypes.Question ? (
                        // For text messages, show textarea input with Save button
                        <div className="mb-4">
                          {/* Textarea input */}
                          <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                            <textarea
                              rows={6}
                              value={editingTextareaValue}
                              onChange={(e) => setEditingTextareaValue(e.target.value)}
                              placeholder="Message Coach Logic..."
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey) {
                                  e.preventDefault()
                                  handleSaveEdit()
                                } else if (e.key === 'Escape') {
                                  e.preventDefault()
                                  handleCancelEdit()
                                }
                              }}
                              className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[120px]"
                            />
                            <div className="flex justify-end mt-2">
                              <AppButton
                                variant="primary"
                                size="sm"
                                label="Save"
                                disabled={!editingTextareaValue?.trim() || processing}
                                onClick={handleSaveEdit}
                              />
                            </div>
                          </div>
                        </div>
                      ) : (message.editingStepType === OnboardingFlowStepTypes.Form ||
                          message.editingStepType === OnboardingFlowStepTypes.GoalTemplate) &&
                        message.editingStepContent ? (
                        // Render the form for editing
                        <div className="mb-4">
                          <AppDynamicChatMessage
                            content={message.editingStepContent}
                            onSubmit={(formData) => {
                              handleEditFormSubmit(formData)
                            }}
                            typingAnimationCallback={() => {}}
                            useTypingAnimation={false}
                            isLastMessage={true}
                            isSendingMessageTobot={false}
                            tools={tools}
                            setIsUpload={setIsUpload}
                            showUploadModal={showUploadModal}
                            isUpload={isUpload}
                            uploadedFile={uploadedFile}
                            setUploadedFile={setUploadedFile}
                            startUpload={startUpload}
                            setStartUpload={setStartUpload}
                            scrollToBottom={scrollToBottom}
                            refetchBusinessGoal={refetchBusinessGoal}
                            refetchTool={refetch}
                          />
                        </div>
                      ) : null}

                      {/* Warning banner */}
                      <div className="p-3 bg-app-primary-10 border border-app-primary-25 rounded-lg text-sm text-app-primary-100">
                        <div className="flex items-start gap-2">
                          <span className="text-app-primary-125 font-bold">!</span>
                          <span>
                            Editing this message may update Coach Logic's subsequent responses
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              )
            }

            if (message.text) {
              // Check if there's an active editing message for this user message
              const hasActiveEditing = messages.some(
                (msg) => msg.isEditingMessage && msg.editingOriginalMessageIndex === index
              )
              return (
                <React.Fragment key={messageKey}>
                  <div
                    className={`flex gap-2 ${
                      message.sender === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                      <AppAvatar
                        size={'md'}
                        type={!logo && message.sender === 'user' ? 'text' : 'image'}
                        shape={'Rounded'}
                        text={userInitial}
                        color="Primary"
                        src={message.sender === 'user' ? logo : Avatar}
                      />
                    </div>
                    {/* Regular message bubble with edit controls */}
                    <div className="w-fit max-w-[90%] flex flex-col">
                      <div
                        className={`flex rounded-lg px-3 pt-2 pb-1.5 text-base text-black-appDark !font-inter ${
                          message.sender === 'assistant'
                            ? 'bg-background-appLight text-black'
                            : 'bg-primary-appBrand text-white'
                        }`}
                      >
                        {message.sender === 'assistant' && message.text && isLastMessage ? (
                          <TypingMessage text={message.text} onNewCharacter={handleTypingMessage} />
                        ) : message.sender === 'assistant' ? (
                          <div className="chatbot-markdown">
                            <Markdown
                              rehypePlugins={[rehypeRaw] as any}
                              remarkPlugins={[remarkGfm, remarkMath]}
                            >
                              {message.text}
                            </Markdown>
                          </div>
                        ) : (
                          <>{message.text}</>
                        )}
                      </div>
                      {/* Edit button and Copy button directly under message bubble */}
                      {message.sender === 'user' && (
                        <div className="flex items-center gap-2 justify-end">
                          {isLastUserMessage && !hasActiveEditing && !processing && !disableButton && (
                            <>
                              <button
                                onClick={handleEditLastMessage}
                                className="mt-3"
                                title="Edit message"
                              >
                                <PenIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={async () => {
                                  if (message.text) {
                                    try {
                                      await copyToClipboard(message.text)
                                    } catch (err) {
                                      console.error('Failed to copy: ', err)
                                    }
                                  }
                                }}
                                className="mt-3"
                                title="Copy message"
                              >
                                <CopyIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                          {message.isEdited && (
                            <div className="flex items-center gap-1">
                              <div className="text-xs text-gray-400 italic mt-3">
                                Edited message
                              </div>
                              <svg
                                className="w-3 h-3 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                                />
                              </svg>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {showVoicePlayback && (
                    <div className="flex justify-start ml-12">
                      <AppVoicePlayback
                        isActive={isVoiceActive}
                        isPlaying={isVoicePlaying}
                        duration={isVoiceActive ? activeVoiceDuration : undefined}
                        onToggle={() => toggleVoicePlayback(messageKey, assistantVoiceText)}
                      />
                    </div>
                  )}
                </React.Fragment>
              )
            }
            if (message.content) {
              return (
                <React.Fragment key={messageKey}>
                  <div
                    className={`flex gap-2 ${
                      message.sender === 'user' ? 'justify-end' : 'justify-start'
                    }`}
                  >
                    <div className={`${message.sender === 'user' ? 'order-2' : ''}`}>
                      <AppAvatar
                        size={'md'}
                        type={!logo && message.sender === 'user' ? 'text' : 'image'}
                        shape={'Rounded'}
                        text={userInitial}
                        color="Primary"
                        src={message.sender === 'user' ? logo : Avatar}
                      />
                    </div>
                    <div className="flex-1">
                      <AppDynamicChatMessage
                        key={index}
                        content={{ ...message.content }}
                        onSubmit={submitDataConfirmationForm}
                        typingAnimationCallback={handleTypingMessage}
                        useTypingAnimation={index === messages.length - 1}
                        isLastMessage={isLastMessage}
                        isSendingMessageTobot={isSendingMessageTobot}
                        tools={tools}
                        setIsUpload={setIsUpload}
                        showUploadModal={showUploadModal}
                        isUpload={isUpload}
                        uploadedFile={uploadedFile}
                        setUploadedFile={setUploadedFile}
                        startUpload={startUpload}
                        setStartUpload={setStartUpload}
                        scrollToBottom={scrollToBottom}
                        refetchBusinessGoal={refetchBusinessGoal}
                        refetchTool={refetch}
                        addMessage={addMessage}
                      />
                      {message.isEdited && (
                        <div className="text-xs text-gray-500 mt-1 italic">(edited)</div>
                      )}
                    </div>
                    {showContentVoicePlayback && (
                      <div className="flex justify-start ml-12">
                        <AppVoicePlayback
                          isActive={isVoiceActive}
                          isPlaying={isVoicePlaying}
                          duration={isVoiceActive ? activeVoiceDuration : undefined}
                          onToggle={() =>
                            toggleVoicePlayback(messageKey, assistantContentVoiceText)
                          }
                        />
                      </div>
                    )}
                  </div>
                </React.Fragment>
              )
            }
            return <></>
          })}
          <div className="text-center w-full bg-app-grey-5 font-normal font-inter text-xs text-primary-appBrand flex justify-center cursor-pointer">
            {!(processing || disableButton || !messages.length) && showGetHelpButton && (
              <div className="chatbot-help py-1 px-2 hover:bg-blue" onClick={helpDesk}>
                Get Help?
              </div>
            )}
          </div>
          {processing ? <AppTypingIndicator /> : <></>}
        </div>
        <div className="p-4 sm:p-6 pt-0">
          {(() => {
            const editingMessage = messages.find((msg) => msg.isEditingMessage)
            const isEditing = !!editingMessage

            return (
              <div className="flex flex-col rounded-lg px-4 py-3 border border-primary-appDarkBorder">
                <textarea
                  disabled={
                    processing || disableButton || isFormStep || isToolConnectionStep || isEditing
                  }
                  rows={4}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Message Coach Logic..."
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage()
                    }
                  }}
                  className="w-full resize-none font-inter text-base text-black-appDark leading-6 bg-transparent border-none focus-visible:ring-0 focus-visible:outline-none min-h-[80px]"
                />
                <div className="flex justify-between relative w-full">
                  <div className="flex gap-2 items-center">
                    <AppButton
                      variant="clear"
                      size="sm"
                      RightIcon={AppPaperClipIcon}
                      disabled={
                        !isUpload ||
                        processing ||
                        isSendingMessageTobot ||
                        startUpload ||
                        isToolConnectionStep
                      }
                      onClick={() => setShowUploadModal(!showUploadModal)}
                    />
                  </div>

                  {processing ? (
                    <CircleNotchIcon className={'fill-primary h-[30px]'} />
                  ) : (
                    <AppButton
                      variant="primary"
                      size="sm"
                      RightIcon={AppSendMessageIcon}
                      disabled={processing || disableButton || inputValue?.trim() === ''}
                      onClick={() => handleSendMessage()}
                    />
                  )}
                </div>
              </div>
            )
          })()}
        </div>
        {showUploadModal && (
          <AppModalBox title={'Upload File'} onClose={() => setShowUploadModal(false)}>
            <div className="m-10">
              <AppFileUploadField
                name="imageData"
                className="flex-1 "
                labelFontSize="base"
                onChange={(e) => {
                  setUploadedFile(e)
                }}
              />
              <div className="mt-20 w-[30%]">
                <AppButton
                  variant="primary"
                  size="sm"
                  label="Upload"
                  disabled={!uploadedFile}
                  onClick={() => {
                    setStartUpload(true)
                    setShowUploadModal(false)
                  }}
                />
              </div>
            </div>
          </AppModalBox>
        )}
      </div>
    </AppModalBox>
  )
}
